"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { Chess, type Square } from "chess.js";
import { BoardView } from "@/components/board/BoardView";
import type { MoveIntent } from "@/components/board/interaction";
import type { Demo } from "@/content/types";
import { meetsExpect, OPEN_ENDED } from "@/lib/chess/demo";
import { playMoveSound } from "@/lib/sound";

type Status = { kind: "idle" | "good" | "wrong" | "trap" | "done"; text: string };

/** A position with a task. The reader plays their moves; scripted replies follow. */
export function DemoBoard({ demo, view = "3d", onViewChange }: { demo: Demo; view?: "2d" | "3d"; onViewChange?: (view: "2d" | "3d") => void }) {
  const [chess, setChess] = useState(() => new Chess(demo.fen));
  const [fen, setFen] = useState(demo.fen);
  const [step, setStep] = useState(0);
  const [last, setLast] = useState<{ from: Square; to: Square } | null>(null);
  const [status, setStatus] = useState<Status>({ kind: "idle", text: demo.prompt });
  const [hint, setHint] = useState(false);
  const [busy, setBusy] = useState(false);
  const locked = useRef(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => {
    if (timer.current) clearTimeout(timer.current);
  }, []);

  const orientation = demo.orientation ?? (new Chess(demo.fen).turn() === "w" ? "white" : "black");
  const userColor = new Chess(demo.fen).turn() === "w" ? "white" : "black";

  const reset = useCallback(() => {
    if (timer.current) clearTimeout(timer.current);
    const c = new Chess(demo.fen);
    setChess(c);
    setFen(c.fen());
    setStep(0);
    setHint(false);
    setBusy(false);
    locked.current = false;
    setLast(null);
    setStatus({ kind: "idle", text: demo.prompt });
  }, [demo]);

  const onMove = (m: MoveIntent): boolean => {
    if (locked.current || status.kind === "done" || status.kind === "trap") return false;
    setHint(false);
    const uci = m.from + m.to + (m.promotion ?? "");
    // One Chess instance per attempt keeps the move history, which repetition checks need.
    const c = chess;
    let mv;
    try {
      mv = c.move({ from: m.from, to: m.to, promotion: m.promotion });
    } catch {
      setStatus({ kind: "wrong", text: demo.traps?.[uci] ?? "That move is not legal in this position. Select your piece and choose a highlighted destination." });
      return false;
    }
    setFen(c.fen());
    setLast({ from: mv.from, to: mv.to });
    playMoveSound(mv.captured ? "capture" : "move");

    const finishIfMet = () => {
      if (!demo.expect || meetsExpect(c, mv, demo.expect)) {
        setStatus({ kind: "done", text: demo.success });
        return true;
      }
      return false;
    };

    if (demo.anyMove) {
      if (!finishIfMet()) setStatus({ kind: "wrong", text: "That move does not complete the task. Reset and try again." });
      return true;
    }

    const expected = demo.line[step];
    const isLastUserMove = step >= demo.line.length - 1;
    const openEndedHit = isLastUserMove && demo.expect && OPEN_ENDED.includes(demo.expect) && meetsExpect(c, mv, demo.expect);

    if (uci === expected || openEndedHit) {
      const reply = demo.line[step + 1];
      if (!reply || openEndedHit) {
        if (!finishIfMet()) setStatus({ kind: "done", text: demo.success });
        return true;
      }
      setStatus({ kind: "good", text: "Good. Watch the reply…" });
      locked.current = true; setBusy(true);
      timer.current = setTimeout(() => {
        locked.current = false; setBusy(false);
        const r = c.move({ from: reply.slice(0, 2), to: reply.slice(2, 4), promotion: reply[4] });
        setFen(c.fen());
        setLast({ from: r.from, to: r.to });
        playMoveSound(r.captured ? "capture" : "move");
        const nextStep = step + 2;
        setStep(nextStep);
        if (nextStep < demo.line.length) setStatus({ kind: "good", text: "Your move again." });
        else if (!finishIfMet()) setStatus({ kind: "done", text: demo.success });
      }, 550);
      return true;
    }

    const trap = demo.traps?.[uci];
    setStatus(trap ? { kind: "trap", text: trap } : { kind: "wrong", text: "Not quite. The move is undone, try again." });
    if (!trap) {
      locked.current = true; setBusy(true);
      timer.current = setTimeout(() => {
        locked.current = false; setBusy(false);
        c.undo();
        setFen(c.fen());
        setLast(null);
      }, 700);
    }
    return true;
  };

  const nextMove = demo.line[step];
  const totalTurns = Math.ceil(demo.line.length / 2);
  const completedTurns = status.kind === "done" ? totalTurns : Math.floor(step / 2);
  const hintMove = hint && !busy && nextMove ? { from: nextMove.slice(0, 2) as Square, to: nextMove.slice(2, 4) as Square, color: "#a9533bcc" } : null;
  return <div className="lesson-demo-layout">
    <BoardView cameraPreset="club" view={view} onViewChange={onViewChange} fen={fen} orientation={orientation} movable={busy || status.kind === "done" || status.kind === "trap" ? "none" : userColor} lastMove={last} onMove={onMove} arrows={hintMove ? [hintMove] : []} label={demo.title} />
    <div className="lesson-demo-coach"><p className="lesson-eyebrow">Your turn · {userColor} to play</p><h3>{demo.title}</h3><div className="lesson-demo-status" role="status" aria-live="polite">{status.kind === "done" && <strong>Well played. </strong>}{status.text}</div>
      {totalTurns > 1 && <div className="practice-sequence"><div><span>{completedTurns} of {totalTurns} moves practised</span><span>{busy ? "Watch the reply" : status.kind === "done" ? "Sequence complete" : "Your move"}</span></div><progress max={totalTurns} value={completedTurns} aria-label="Opening sequence progress" /></div>}
      {demo.moveNotes?.[step] && status.kind !== "done" && <aside className="practice-plan"><p className="lesson-eyebrow">The purpose of this move</p><p>{demo.moveNotes[step]}</p></aside>}
      {hintMove && <p className="lesson-demo-note" role="status">Look at {hintMove.from}. Try moving that piece to {hintMove.to}{nextMove[4] ? ` and promoting to ${nextMove[4] === "q" ? "a queen" : nextMove[4] === "n" ? "a knight" : nextMove[4] === "r" ? "a rook" : "a bishop"}` : ""}.</p>}
      <div className="lesson-demo-actions">{nextMove && status.kind !== "done" && status.kind !== "trap" && <button disabled={busy} aria-pressed={hint} onClick={() => setHint(!hint)}>{hint ? "Hide hint" : "Show me a hint"}</button>}<button onClick={reset}>{status.kind === "done" ? "Practice again" : "Reset position"}</button></div>
      <p className="lesson-demo-note">Select a piece, then its destination. Switch to 2D for a clear overhead view or rotate the 3D board to explore the position.</p>
    </div>
  </div>;
}


